import React from 'react';
import { Box, Button, ButtonBase, ButtonProps, Tooltip } from '@mui/material';
import { FeatureProps } from './types';
import HasFeature from './HasFeature';
import { RiAddCircleFill, RiLock2Fill } from 'react-icons/ri';
import { useTranslation } from 'services/localization';

export interface FeatureButtonProps extends ButtonProps {
	requiredFeature?: FeatureProps['requiredFeature'];
	filteringType?: FeatureProps['filteringType'];
	onPermissionsMissingClick?: () => void;
	onFeaturesMissingClick?: () => void;
}
const FeatureButton = (props: FeatureButtonProps) => {
	const {
		requiredFeature,
		filteringType,
		onPermissionsMissingClick,
		onFeaturesMissingClick,
		...buttonProps
	} = props;
	const featureProps = { requiredFeature, filteringType };

	return (
		<HasFeature
			{...featureProps}
			fallback={(failedChecks) => {
				if (failedChecks.user && failedChecks.shop) return null;

				return failedChecks.user ? (
					<LockedButton {...buttonProps} onClick={onPermissionsMissingClick} />
				) : (
					<HighlightButton {...buttonProps} onClick={onFeaturesMissingClick} />
				);
			}}
		>
			<Button {...buttonProps} />
		</HasFeature>
	);
};

export const LockedButton = (props: ButtonProps & { tooltipText?: string }) => {
	const { t } = useTranslation();
	const { tooltipText, ...buttonProps } = props;

	return (
		<Tooltip
			title={
				tooltipText ??
				t('permissions.notPermitted', "You don't have the required permissions to do this")
			}
		>
			<Box
				display="inline-block"
				sx={{ position: 'relative', width: buttonProps.fullWidth ? '100%' : 'auto' }}
			>
				<Button {...buttonProps} disabled startIcon={<RiLock2Fill size={16} />} />
			</Box>
		</Tooltip>
	);
};

export const HighlightButton = (props: ButtonProps) => {
	return (
		<ButtonBase
			sx={{
				position: 'relative',
				display: 'inline-block',
				width: props.fullWidth ? '100%' : 'auto',
			}}
			disabled={!props.onClick}
			focusRipple
			onClick={props.onClick}
		>
			<>
				<Button {...props} disabled />
				<Box
					sx={{
						display: 'inline',
						position: 'absolute',
						top: -6,
						right: -6,
						color: (theme) => theme.palette.success.main,
						borderRadius: '50%',
						background: (theme) => theme.palette.background.paper,
						width: 24,
						height: 24,
					}}
				>
					<RiAddCircleFill size={24} />
				</Box>
			</>
		</ButtonBase>
	);
};

export default FeatureButton;
